import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  InputGroup,
  Table,
  Form
} from 'react-bootstrap';
import Paging from 'components/common-sfims/pagination/paging';
import listColumn from 'components/pages/haccp/standardInfo/ingredients/column/list';

// api
import { registIrdntList } from 'api/haccp/standardInfo/ingredientsApi';
import { factory, category } from 'api/haccp/commonApi';
// function
import { getLoginInform } from 'function/login';
import { datas2SfshExcelJSON, utc2tz } from 'function/convert';
import { downloadExcelByJSON } from 'function/file';
// types
import { USE_YN_SELECT } from 'types/type';
import { RESPONSE_CODE } from 'types/const';
// util
import format from 'utils/string/format';
import comma from 'utils/format/comma';
// hooks
import useHistoryState from 'hooks/useHistoryState';

/**
 *  @description 등록성분량관리 목록
 */

const List = () => {
  /* router info */
  const history = useHistory();
  const { state } = useLocation();

  /* variable */
  const title = '등록성분량 정보';

  /* state */
  const [adminYn, setAdminYn] = useState(null); // Y: 관리자, N: 이천,군산 담당자
  const [totalCount, setTotalCount] = useState(0);
  const [factoryOptions, setFactoryOptions] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState(null);
  const [list, setList] = useState([]);
  const [listStartNumber, setListStartNumber] = useState(0);

  // 조회조건
  const [pltCd, setPltCd] = useHistoryState('', 'pltCd'); // 공장
  const [ctgryGrpCd, setCtgryGrpCd] = useHistoryState('', 'ctgryGrpCd'); // 구분: 제품,공정,원료
  const [useYn, setUseYn] = useHistoryState('', 'useYn'); // 사용유무
  const [divisionKey, setDivisionKey] = useHistoryState('', 'divisionKey'); // input 조건 선택
  const [divisionValue, setDivisionValue] = useHistoryState(
    '',
    'divisionValue'
  );

  const [searchParams, setSearchParams] = useHistoryState(
    {
      page: 0,
      size: 12,
      sort: 'seq,desc',
      ...state
    },
    'searchParams'
  );

  /* 등록성분량 등록 및 상세페이지 이동 */
  const onDetail = seq => {
    history.push({
      pathname: format('/haccp/ingredient/detail/{seq}', { seq }),
      state: searchParams
    });
  };

  /* 엑셀다운로드 */
  const onExcelDown = async () => {
    const { pltCd, ctgryGrpCd, useYn, divisionKey, divisionValue, sort } =
      searchParams || {};

    const params = {
      pltCd: pltCd,
      ctgryGrpCd: ctgryGrpCd,
      useYn: useYn,
      [divisionKey]: divisionValue,
      sort: sort
    };

    Object.keys(params)?.forEach(
      key => (!params[key] || params[key] === 'ALL') && delete params[key]
    );

    const response = await registIrdntList(params);

    const { code, list = [], totalCount = 0 } = response || {};

    if (code !== RESPONSE_CODE.Success || totalCount === 0) return;

    let excelNumber = totalCount;

    downloadExcelByJSON(
      datas2SfshExcelJSON(
        list?.map(e => ({
          ...e,
          no: excelNumber--,
          regDt: utc2tz(e.regDt, 'YYYY-MM-DD')
        })),
        listColumn
      ),
      `${title}.xlsx`
    );
  };

  /* 조회버튼 */
  const onSearch = () => {
    setSearchParams({
      ...searchParams,
      page: 0,
      pltCd: pltCd,
      ctgryGrpCd: ctgryGrpCd,
      useYn: useYn,
      divisionKey: divisionKey,
      divisionValue: divisionValue
    });
  };

  /* 조회API 요청 */
  const onSearchRequest = async () => {
    const {
      pltCd,
      ctgryGrpCd,
      useYn,
      divisionKey,
      divisionValue,
      page,
      size,
      sort
    } = searchParams || {};

    const params = {
      pltCd: pltCd,
      ctgryGrpCd: ctgryGrpCd,
      useYn: useYn,
      [divisionKey]: divisionValue,
      page: page,
      size: size,
      sort: sort
    };

    Object.keys(params)?.forEach(
      key => (!params[key] || params[key] === 'ALL') && delete params[key]
    );

    const response = await registIrdntList(params);

    const { code, list = [], totalCount } = response || {};

    if (code !== RESPONSE_CODE.Success) return;

    // 목록 페이지 Start No.
    setListStartNumber(totalCount - searchParams.size * searchParams.page);
    setTotalCount(totalCount);
    setList(list);
  };

  /* 공장목록 API */
  const factoryList = async () => {
    const response = await factory();

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;

    setFactoryOptions(list);
  };

  /* 분류목록 API */
  const categoryList = async () => {
    const response = await category();

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;

    setCategoryOptions(list);
  };

  // 로그인 정보
  const loginInfo = () => {
    const { adminYn, pltCd } = getLoginInform();

    if (adminYn === 'Y') {
      setAdminYn(adminYn);
      return;
    }

    setPltCd(pltCd);
    setSearchParams({
      ...searchParams,
      page: 0,
      pltCd: pltCd
    });
    setAdminYn(adminYn);
  };

  /* watch */
  useEffect(() => {
    // 공장 API 호출
    factoryList();
    // 분류 API 호출
    categoryList();
    // 로그인정보
    loginInfo();
  }, []);

  useEffect(() => {
    if (adminYn === null) return;
    onSearchRequest();
  }, [searchParams, adminYn]);

  return (
    <>
      <Card>
        <Card.Header>
          <Row className="align-items-center g-2">
            <Col>
              <h5 className="mb-0">{title}</h5>
            </Col>
            <Col xs={'auto'}>
              <Button
                size="sm"
                variant="secondary"
                onClick={onExcelDown}
                disabled={totalCount === 0}
              >
                엑셀 다운로드
              </Button>
              <Button size="sm" onClick={() => onDetail()} className="ms-3">
                등록성분량 등록
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="pt-0">
          <Form.Group className="mb-3">
            <Row className="ms-0 me-0 mt-3">
              {/* 공장 선택 select-box */}
              <Col sm={2} className="ps-1 pe-1">
                <Form.Select
                  size="sm"
                  onChange={({ target: { value } }) => setPltCd(value)}
                  value={pltCd}
                  disabled={adminYn === 'N'}
                >
                  <option disabled value="">
                    공장선택
                  </option>
                  <option value={'ALL'}>전체</option>
                  {factoryOptions?.map(option => (
                    <option key={option.pltCd} value={option.pltCd}>
                      {option.pltNmKo}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              {/* 사용여부 Select Box*/}
              <Col sm={2} className="ps-1 pe-1">
                <Form.Select
                  size="sm"
                  onChange={({ target: { value } }) => setUseYn(value)}
                  value={useYn}
                >
                  <option disabled value="">
                    사용유무 선택
                  </option>
                  <option value={'ALL'}>전체</option>
                  {USE_YN_SELECT.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.value}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              {/* 구분 Select Box*/}
              <Col sm={2} className="ps-1 pe-1">
                <Form.Select
                  required
                  size="sm"
                  onChange={({ target: { value } }) => setCtgryGrpCd(value)}
                  value={ctgryGrpCd}
                >
                  <option hidden>구분선택</option>
                  {categoryOptions?.map(option => (
                    <option key={option.ctgryCd} value={option.ctgryCd}>
                      {option.ctgryNmKr}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              {/* 선택 및 입력창 */}
              <Col sm={4} className="ps-1 pe-1">
                <InputGroup size="sm" className="w-100">
                  <Form.Select
                    size="sm"
                    className="w-25"
                    value={divisionKey}
                    onChange={({ target: { value } }) => setDivisionKey(value)}
                    disabled={ctgryGrpCd === ''}
                  >
                    <option hidden>선택</option>
                    <option value={'ctgry1Nm'}>축종명</option>
                    <option value={'ctgry2Nm'}>등록명칭명</option>
                    <option value={'ctgry3Nm'}>등록명칭번호명</option>
                  </Form.Select>
                  <Form.Control
                    placeholder="구분선택 후 입력해주세요."
                    type="text"
                    size="sm"
                    className="w-50"
                    maxLength="20"
                    value={divisionValue}
                    onChange={({ target: { value } }) =>
                      setDivisionValue(value)
                    }
                    disabled={divisionKey === ''}
                  />
                </InputGroup>
              </Col>
              <Col
                sm={'auto'}
                className="ps-1 pe-1 d-flex flex-row align-items-center"
              >
                <Button size="sm" onClick={onSearch}>
                  조회
                </Button>
                <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                  검색 결과 : {comma(totalCount)} 건
                </p>
              </Col>
            </Row>
          </Form.Group>
          <Table bordered hover className="fs--1 text-center">
            <thead className="bg-200 text-700 align-middle border-300">
              <tr>
                <th className="text-nowrap">No.</th>
                <th className="text-nowrap">공장</th>
                <th className="text-nowrap">구분</th>
                <th className="text-nowrap">축종</th>
                <th className="text-nowrap">등록명칭</th>
                <th className="text-nowrap">등록명칭번호</th>
                <th className="text-nowrap">성분등록정보</th>
                <th className="text-nowrap">사용유무</th>
                <th className="text-nowrap">설정일</th>
              </tr>
            </thead>
            <tbody>
              {list?.map(
                (
                  {
                    seq,
                    pltNm,
                    ctgryGrpNm,
                    ctgry1Nm,
                    ctgry2Nm,
                    ctgry3Nm,
                    subInfo,
                    useYn,
                    regDt
                  },
                  index
                ) => (
                  <tr
                    key={index}
                    onClick={() => onDetail(seq)}
                    className="cursor-pointer"
                  >
                    <td>{listStartNumber - index}</td>
                    <td>{pltNm}</td>
                    <td>{ctgryGrpNm}</td>
                    <td>{ctgry1Nm}</td>
                    <td>{ctgry2Nm}</td>
                    <td>{ctgry3Nm || '-'}</td>
                    <td>{subInfo}</td>
                    <td>{useYn}</td>
                    <td>{utc2tz(regDt, 'YYYY-MM-DD') || '-'}</td>
                  </tr>
                )
              )}
              {list.length < 1 && (
                <tr>
                  <td colSpan={9} className="w-auto">
                    등록성분량 정보가 존재하지 않습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {totalCount > 0 && (
            <Paging
              key={totalCount}
              perPage={searchParams.size}
              length={10}
              currentPage={searchParams.page + 1}
              totalCount={totalCount}
              movePage={page =>
                setSearchParams({ ...searchParams, page: page - 1 })
              }
            ></Paging>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default List;
